import { useState } from 'react';
import { Phone, Copy, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import useCustomerIO from '@/hooks/useCustomerIO';

interface UpdatesActionsProps {
  orderUid: string;
  phone?: string | null;
  canResolve: boolean;
  /** False when the order header already shows its own Resolve. */
  showResolve?: boolean;
  onResolve: () => void;
}

/** PRD §5.6 — Call (number + Copy + tel: on web) and Resolve (existing merchant-action flow). */
export function UpdatesActions({
  orderUid,
  phone,
  canResolve,
  showResolve = true,
  onResolve,
}: UpdatesActionsProps) {
  const { t } = useTranslation();
  const { trackEvent } = useCustomerIO();
  const [copied, setCopied] = useState(false);
  const number = (phone ?? '').replace(/\s+/g, '');

  const copy = () =>
    navigator.clipboard
      ?.writeText(number)
      .then(() => setCopied(true))
      .catch(() => undefined);

  const resolve = () => {
    trackEvent('updates_resolve_clicked', { order_uid: orderUid });
    onResolve();
  };

  const resolveLabel = String(t('updates_resolve'));

  return (
    <div className='flex shrink-0 flex-wrap items-center gap-2'>
      <Popover onOpenChange={(open) => (open ? setCopied(false) : undefined)}>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            size='sm'
            onClick={() => trackEvent('updates_call_customer_clicked', { order_uid: orderUid })}
          >
            <Phone aria-hidden />
            {String(t('updates_call_customer'))}
          </Button>
        </PopoverTrigger>
        <PopoverContent align='end' className='w-64 space-y-3'>
          <p dir='ltr' className='m-0 w-fit text-start text-[15px] font-semibold tabular-nums'>
            {number || '—'}
          </p>
          <div className='flex gap-2'>
            <Button variant='outline' size='sm' onClick={copy} disabled={!number}>
              {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
              {copied ? String(t('copied')) : String(t('updates_copy_phone'))}
            </Button>
            {number ? (
              <Button size='sm' asChild>
                <a href={`tel:${number}`}>
                  <Phone aria-hidden />
                  {String(t('updates_call_customer'))}
                </a>
              </Button>
            ) : null}
          </div>
        </PopoverContent>
      </Popover>
      {!showResolve ? null : canResolve ? (
        <Button size='sm' onClick={resolve}>
          {resolveLabel}
        </Button>
      ) : (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                tabIndex={0}
                className='inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-ring'
              >
                <Button size='sm' disabled>
                  {resolveLabel}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{String(t('updates_no_action_needed'))}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}
